import { Icon } from './Icon';
import { STATUS_LOOK, PRIORITY_LABELS, type DisplayStatus } from '../taskVocab';

/**
 * A status, drawn.
 *
 * Two forms from one definition: the full badge for a detail panel or a picker option, and the
 * icon-only mark for a dense row where the column or the position already says the state. Both
 * carry the word — the full one prints it, the compact one puts it in `title` and `aria-label` —
 * so the glyph never has to be understood on its own.
 */
export function StatusBadge({
  status,
  compact = false,
}: {
  status: DisplayStatus;
  compact?: boolean;
}) {
  const look = STATUS_LOOK[status];
  if (compact) {
    return (
      <span className={`badge mark-only tone-${look.tone}`} title={look.label}>
        <Icon name={look.icon} size={15} label={look.label} />
      </span>
    );
  }
  return (
    <span className={`badge tone-${look.tone}`}>
      <Icon name={look.icon} size={12} />
      {look.label}
    </span>
  );
}

/**
 * Priority, as a rising level.
 *
 * `Medium` was a 58-pixel word on every row from a set of three, and three dots said the same
 * thing in 24 — but three dots is `•••`, the universal overflow-menu glyph. Two personas filed it
 * and one of them clicked it expecting actions; on the Tasks list it is in fact the *only* thing
 * that looks like a menu, because the row's real menu is the right-click. Two screens over, the
 * goal row uses the identical mark as a genuine menu, so one glyph meant two things.
 *
 * Bars of rising height instead. The level is in the shape — how many are filled *and* how tall
 * the last one is — so it survives greyscale and does not need the tooltip it used to need. Same
 * three elements, narrower than the dots were, and unmistakably not a menu.
 */
export function PriorityMark({ priority }: { priority: number }) {
  if (priority <= 0) return null;
  const label = `${PRIORITY_LABELS[priority] ?? priority} priority`;
  return (
    <span className="mark priority" data-priority={priority} title={label} aria-label={label}>
      {[1, 2, 3].map((level) => (
        <i key={level} className={level <= priority ? 'on' : ''} />
      ))}
    </span>
  );
}
